import { useEffect, useState } from 'react';
import axios from 'axios';
import { useClassroomStore } from '../store';
import { submitResponse } from '../classroomApi';
import type { ActivityStep } from '../classroomApi';
import ChoiceResponse from './ChoiceResponse';
import NumericResponse from './NumericResponse';
import TextResponse from './TextResponse';
import ConfidenceButtons from './ConfidenceButtons';
import type { ConfidenceValue } from './ConfidenceButtons';
import WidgetAnswer from './WidgetAnswer';
import { useWidgetSnapshot } from './StepPrompt';
import { AnswerReceipt, VotingClosedNotice, describeAnswer } from './AnswerReceipt';
import { widgetRefsForStep } from '../widgetForStep';
import { WIDGET_ANSWER_TYPES } from '../../lab/WidgetRenderer';

// BP 22 student surface — extracted from features/classroom/index.tsx
// (WIP-018, build order §2.3) so the rest of the live-activity set can add a
// step-mode-specific row above/below the built-in controls without editing
// index.tsx's `LiveStep` again. A step carries no explicit "answer shape"
// field — the control is inferred from the step's own data.
interface ResponseControlProps {
  activityId: string;
  step: ActivityStep;
  submitted: boolean;
}

export default function ResponseControl({ activityId, step, submitted }: ResponseControlProps) {
  const refresh = useClassroomStore((s) => s.refresh);
  const myResponse = useClassroomStore((s) => s.myResponse(step.key));
  // WIP-011 (design §1, §4.3) — the live activity IS this step's activity
  // (ResponseControl only ever renders for the current live step), so its own
  // `results_shown` is exactly "is voting closed for what's on screen right
  // now". Read from the store directly rather than a new prop: `index.tsx`'s
  // `LiveStep` is frozen for this issue (build order §2.3/§2.5 — its slot is
  // the header chip, not this control), and the store already carries it.
  const resultsShown = useClassroomStore((s) => s.activity?.results_shown ?? false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // WIP-012 (design §5.1) — confidence is one extra tap that never delays or
  // gates the primary answer. The single-choice poll is the common case: the
  // tap submits `{choice}` immediately (ChoiceResponse), same as before this
  // issue touched the file; confidence is asked ONLY after that answer is
  // recorded, as a second, separate re-submit of the same payload with
  // `confidence` added (an upsert on the same row — see handleConfidence).
  const isSingleChoice = !!(step.options && step.options.length > 0 && !step.multi);

  // Multi-select / numeric / text ask beside their own explicit Submit
  // instead: captured locally, folded into that same submit when it happens,
  // never a separate round trip. Resets when the step underneath changes.
  const [pendingConfidence, setPendingConfidence] = useState<ConfidenceValue | null>(null);
  useEffect(() => setPendingConfidence(null), [step.key]);

  // WIP-016 (design §8.3) — `widget_refs` live on the activity, not the step,
  // so it's read straight off the shared poll (already in the store; adding
  // no new selector to the frozen `store.ts`, build order §5) rather than
  // threaded down from the frozen `index.tsx`. Not every widget is
  // answerable this way — only `WIDGET_ANSWER_TYPES` — so an author setting
  // `answer: 'widget'` on a step whose widget_ref isn't one of the two that
  // implement the contract falls straight through to the controls below,
  // exactly as if `answer` were absent.
  // Step-level widget_refs win over the activity's, same resolution as
  // StepPrompt and LiveQuestion (widgetForStep.ts) — otherwise a step with
  // its own answer-widget would be validated against the ACTIVITY's widget
  // type and fall through to the plain controls.
  const widgetRefs = widgetRefsForStep(step, useClassroomStore((s) => s.activity?.widget_refs));
  const primaryWidgetRef = widgetRefs[0];
  const isWidgetAnswer = step.answer === 'widget' && !!primaryWidgetRef && WIDGET_ANSWER_TYPES.has(primaryWidgetRef.type);
  const widgetSnapshot = useWidgetSnapshot(step.key);

  const submitPayload = async (payload: Record<string, unknown>) => {
    setSubmitting(true);
    setError(null);
    try {
      await submitResponse(activityId, { step_key: step.key, payload });
      await refresh();
    } catch (err) {
      // A submit that raced the close (design §1) gets the same closed
      // notice as clicking after the fact — never a generic error for it.
      if (axios.isAxiosError(err) && err.response?.data?.error === 'vote_closed') {
        setError(null);
      } else {
        setError('Could not submit — try again.');
      }
      await refresh();
    } finally {
      setSubmitting(false);
    }
  };

  // The answer's own submit. For a single-choice poll this is deliberately
  // NOT merged with any confidence value — a fresh `{choice}` payload here
  // (no `confidence` key) is exactly what makes re-picking the answer clear
  // the confidence back to unselected on the next poll (the confidence
  // belonged to the old answer, per design §5.1): the backend replaces the
  // whole payload column on update, so an omitted key does not survive.
  const handleSubmit = (payload: Record<string, unknown>) => {
    if (step.confidence && !isSingleChoice && pendingConfidence) {
      return submitPayload({ ...payload, confidence: pendingConfidence });
    }
    return submitPayload(payload);
  };

  const recordedConfidence = (myResponse?.payload?.confidence as ConfidenceValue | undefined) ?? null;

  const handleConfidence = (value: ConfidenceValue) => {
    if (!myResponse) return; // no answer recorded yet — nothing to attach it to
    submitPayload({ ...myResponse.payload, confidence: value });
  };

  // /class live redesign (2026-09-11) — the student's own answer, read back
  // off the shared poll, is what both the receipt and the option highlight
  // are built from. Nothing here re-fetches: `myResponse` is already on the
  // 5 s live poll every student on this page is sharing.
  const answerText = describeAnswer(step, myResponse?.payload);
  const recordedChoices = (() => {
    const payload = myResponse?.payload;
    if (!payload) return undefined;
    if (typeof payload.choice === 'number') return [payload.choice];
    if (Array.isArray(payload.choices)) return payload.choices.filter((i): i is number => typeof i === 'number');
    return undefined;
  })();

  // WIP-011 (design §1) — once the instructor shows results the vote is
  // closed server-side too (409 vote_closed), so the controls come down
  // rather than staying up only to fail on tap.
  if (resultsShown) {
    return <VotingClosedNotice answer={answerText} />;
  }

  const confidenceRow = (value: ConfidenceValue | null, onChange: (v: ConfidenceValue) => void) => (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <span className="text-sm text-ws-ink-2">How sure are you?</span>
      <ConfidenceButtons value={value} onChange={onChange} disabled={submitting} />
    </div>
  );

  return (
    <div className="space-y-3">
      {isWidgetAnswer ? (
        <WidgetAnswer
          snapshot={widgetSnapshot}
          disabled={submitting}
          submitted={submitted}
          onSubmit={() =>
            widgetSnapshot &&
            handleSubmit({
              widget: {
                type: primaryWidgetRef!.type,
                state: widgetSnapshot.state,
                // Defensive client-side mirror of the server's 200-char cap
                // (design §8.3/§11.3) — the server validates independently
                // and never trusts this; this just avoids a round trip that
                // is certain to 400.
                display: widgetSnapshot.display.slice(0, 200),
              },
            })
          }
        />
      ) : step.numeric ? (
        <NumericResponse disabled={submitting} submitted={submitted} onSubmit={handleSubmit} />
      ) : step.options && step.options.length > 0 ? (
        <ChoiceResponse
          step={step}
          disabled={submitting}
          submitted={submitted}
          recorded={recordedChoices}
          onSubmit={handleSubmit}
        />
      ) : (
        <TextResponse disabled={submitting} submitted={submitted} onSubmit={handleSubmit} />
      )}
      {step.confidence && !isSingleChoice && confidenceRow(pendingConfidence, setPendingConfidence)}
      {/* The receipt is the answer to "have I answered?", so it sits directly
          under the control rather than below the confidence row it used to
          share a footer with. */}
      {submitted && !submitting && <AnswerReceipt answer={answerText} canChange />}
      {step.confidence && isSingleChoice && submitted && !submitting && confidenceRow(recordedConfidence, handleConfidence)}
      {error && (
        <p role="alert" className="text-sm font-medium text-ws-danger">{error}</p>
      )}
    </div>
  );
}
